<template>
  <footer class="site-footer">
    <div class="footer-shell">
      <div class="footer-lead">
        <p class="eyebrow">Ready when you are.</p>
        <h2>Let’s make accounting click.</h2>
        <router-link class="footer-cta" to="/contact">
          Book a free consultation
          <i class="fas fa-arrow-right" aria-hidden="true"></i>
        </router-link>
      </div>

      <div class="footer-grid">
        <div class="footer-brand">
          <div class="brand-lockup">
            <span class="brand-icon">
              <img src="../assets/logo-x3.png" alt="" />
            </span>
            <strong>AliTutor</strong>
          </div>
          <p>
            Patient, one-on-one online accounting tutoring for college students
            who want clarity—not more jargon.
          </p>
          <a class="email-link" href="mailto:alifortutoring@gmail.com">
            <i class="fas fa-envelope" aria-hidden="true"></i>
            alifortutoring@gmail.com
          </a>
        </div>

        <nav class="footer-nav" aria-label="Footer navigation">
          <p class="footer-label">Start here</p>
          <router-link to="/howitworks">Tutoring Process</router-link>
          <router-link to="/#pricing">Plans &amp; Pricing</router-link>
          <router-link to="/about">About Ali</router-link>
          <router-link to="/contact">Free Consultation</router-link>
        </nav>

        <div class="footer-social">
          <p class="footer-label">Follow</p>
          <div class="social-row">
            <a
              href="https://www.linkedin.com/in/ali-khalil-192904289/"
              aria-label="LinkedIn"
              target="_blank"
              rel="noopener noreferrer"
            ><i class="fab fa-linkedin-in" aria-hidden="true"></i></a>
            <a
              href="https://www.tiktok.com/@ali.tutoring"
              aria-label="TikTok"
              target="_blank"
              rel="noopener noreferrer"
            ><i class="fab fa-tiktok" aria-hidden="true"></i></a>
          </div>
        </div>
      </div>

      <div class="footer-bottom">
        <p>© {{ currentYear }} AliTutor. Operated by Ali Khalil as a sole proprietorship.</p>
        <div>
          <router-link to="/privacy">Privacy</router-link>
          <router-link to="/terms">Terms</router-link>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { ref } from "vue";

const currentYear = ref(new Date().getFullYear());
</script>

<style lang="scss" scoped>
.site-footer {
  padding: 1.25rem;
  background: var(--paper);
}

.footer-shell {
  width: min(1240px, 100%);
  margin: 0 auto;
  overflow: hidden;
  border-radius: 32px;
  background: var(--ink);
  color: #fff;
}

.footer-lead {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 2rem;
  padding: clamp(2.2rem, 6vw, 5rem);
  align-items: end;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  background:
    radial-gradient(circle at 85% 15%, rgba(49, 87, 246, 0.48), transparent 32%),
    var(--ink);

  .eyebrow {
    grid-column: 1 / -1;
    margin: 0;
    color: #9eb1ff;
    font-size: 0.76rem;
    font-weight: 800;
    letter-spacing: 0.13em;
    text-transform: uppercase;
  }

  h2 {
    max-width: 700px;
    margin: 0;
    font-size: clamp(2.2rem, 6vw, 5rem);
    letter-spacing: -0.065em;
    line-height: 0.98;
  }
}

.footer-cta {
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  padding: 1rem 1.2rem;
  border-radius: 14px;
  background: var(--amber);
  color: var(--ink);
  font-size: 0.9rem;
  font-weight: 900;
  white-space: nowrap;
  transition: transform 180ms ease;

  &:hover {
    transform: translateY(-3px);
  }
}

.footer-grid {
  display: grid;
  grid-template-columns: 1.5fr 0.7fr 0.7fr;
  gap: 4rem;
  padding: clamp(2.2rem, 5vw, 4rem);
}

.brand-lockup {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  font-size: 1.3rem;
}

.brand-icon {
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 13px;
  background: #fff;

  img {
    width: 28px;
    height: 28px;
    object-fit: contain;
  }
}

.footer-brand {
  p {
    max-width: 450px;
    margin: 1.2rem 0;
    color: #aeb7ca;
    font-size: 0.9rem;
    line-height: 1.75;
  }
}

.email-link {
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  color: #dce3f2;
  font-size: 0.88rem;
  font-weight: 700;

  &:hover {
    color: var(--amber);
  }
}

.footer-nav {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;

  a {
    color: #c6cede;
    font-size: 0.88rem;
    font-weight: 600;

    &:hover {
      color: #fff;
    }
  }
}

.footer-label {
  margin: 0 0 0.4rem;
  color: #7e8ba5;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.social-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;

  a {
    display: grid;
    width: 42px;
    height: 42px;
    place-items: center;
    border: 1px solid rgba(255, 255, 255, 0.13);
    border-radius: 13px;
    color: #dce3f2;
    transition: all 180ms ease;

    &:hover {
      border-color: var(--blue);
      background: var(--blue);
      color: #fff;
      transform: translateY(-2px);
    }
  }
}

.footer-bottom {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.4rem clamp(2.2rem, 5vw, 4rem);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: #7e8ba5;
  font-size: 0.78rem;

  p {
    margin: 0;
  }

  div {
    display: flex;
    gap: 1.4rem;
  }

  a:hover {
    color: #fff;
  }
}

@media (max-width: 820px) {
  .footer-lead {
    grid-template-columns: 1fr;
    align-items: start;
  }

  .footer-grid {
    grid-template-columns: 1fr 1fr;
  }

  .footer-brand {
    grid-column: 1 / -1;
  }
}

@media (max-width: 560px) {
  .site-footer {
    padding: 0.65rem;
  }

  .footer-shell {
    border-radius: 24px;
  }

  .footer-grid {
    grid-template-columns: 1fr;
    gap: 2.4rem;
  }

  .footer-brand {
    grid-column: auto;
  }

  .footer-bottom {
    flex-direction: column;
  }
}
</style>
